import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { supabase } from "../config/supabaseClient";
import ConfirmationModal from "./ConfirmationModal";

export default function GroupTile({ id, name, openProposals, memberCount, visibility, isAdmin, pendingRequests, onLeaveGroup }) {
  const navigate = useNavigate();
  const [isLeaving, setIsLeaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleLeaveClick = (e) => {
    e.stopPropagation(); // Prevent navigation when clicking the leave button
    setShowConfirmModal(true);
  };

  const handleConfirmLeave = async () => {
    setIsLeaving(true);
    try {
      await onLeaveGroup(id);
    } catch (error) {
      console.error('Error leaving group:', error);
    } finally {
      setIsLeaving(false);
      setShowConfirmModal(false);
    }
  };

  return (
    <>
      <div 
        className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition cursor-pointer relative group"
        onClick={() => navigate(`/groups/${id}`)}
      >
        {/* Pending Join Requests Badge (for admins) */}
        {isAdmin && pendingRequests > 0 && (
          <div className="absolute top-2 right-24 px-2 py-1 bg-primary text-white rounded-full text-sm font-medium flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            {pendingRequests}
          </div>
        )}

        {/* Leave Group Button */}
        <button
          onClick={handleLeaveClick}
          disabled={isLeaving}
          className={`absolute top-2 right-2 px-3 py-1 rounded-lg text-sm border border-red-500 
            text-red-500 hover:bg-red-50 transition opacity-0 group-hover:opacity-100
            ${isLeaving ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isLeaving ? 'Leaving...' : 'Leave'}
        </button>

        <h2 className="text-xl font-bold text-textDark pr-20">{name}</h2>
        <div className="mt-2 flex flex-wrap gap-2">
          {/* Visibility Badge */}
          <div className="inline-block bg-primary/5 rounded-full px-3 py-1">
            <span className="text-sm text-textMed flex items-center">
              {visibility === 'private' ? (
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {visibility === 'private' ? 'Private' : 'Public'}
            </span>
          </div>
          
          {/* Open Proposals Badge */}
          <div className="inline-block bg-primary/5 rounded-full px-3 py-1">
            <span className="text-sm text-textMed flex flex-col items-center text-center">
              <span className="text-base font-semibold leading-none">{openProposals}</span>
              <span className="text-xs leading-tight">open proposals</span>
            </span>
          </div>
          
          {/* Member Count Badge */}
          <div className="inline-block bg-primary/5 rounded-full px-3 py-1">
            <span className="text-sm text-textMed flex flex-col items-center text-center">
              <span className="text-base font-semibold leading-none">{memberCount}</span>
              <span className="text-xs leading-tight">members</span>
            </span>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirmModal}
        title="Leave Group"
        message={`Are you sure you want to leave "${name}"?`}
        confirmText="Leave Group"
        cancelText="Cancel"
        confirmButtonStyle="danger"
        onConfirm={handleConfirmLeave}
        onCancel={() => setShowConfirmModal(false)}
      />
    </>
  );
}

GroupTile.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  openProposals: PropTypes.number.isRequired,
  memberCount: PropTypes.number.isRequired,
  visibility: PropTypes.oneOf(['public', 'private']).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  pendingRequests: PropTypes.number.isRequired,
  onLeaveGroup: PropTypes.func.isRequired
}; 